// CompareFitsPage.jsx
//
// Standalone "Compare Fits" page — own sidebar link (/compare-fits), separate
// from the Optimize tab inside Data Fitting. Madhura, 20-Aug: "Build Compare
// Fits as a graphical overlay of selected saved fits against the data."
//
// Split out of DataFittingPage.jsx's old CompareFitsPanel (see that file's
// comment above the trimmed-down version still living in the Optimize tab,
// which now only handles "Save this fit"). Comparing is a browse-your-
// history action, not a step inside a single fitting run — it doesn't need
// a live `result`, and living on its own route means it isn't reset by
// anything that resets an in-progress run.
//
// Reads/writes fitRecordStore.js — the SAME singleton store DataFittingPage
// writes to, so "Save this fit" there and "Compare" here always agree on
// which reaction_system_id is current (see fit_records_api.py's
// get_default_reaction_system() for how that id is resolved/created).
//
// GRAPHICAL OVERLAY — the new piece. Each saved fit_records row now carries
// its run's predicted_timeseries inside metrics_json (added in
// DataFittingPage.jsx's buildSaveFitPayload — see that file), because
// re-deriving predictions from fitted_parameters at compare time would mean
// knowing which of several forward-model codepaths (generic powerlaw/lhhw,
// AA, DME, MeOAc) produced each fit. Two shapes exist across those
// codepaths (see normalizeSeries below): a single time-series object
// (build_predicted_timeseries in boa_kinetics_objective.py, used by
// batch/vs.-time fits: MeOAc, DME, generic Batch) or a per-row list
// (build_predicted_timeseries_aa in aa_kinetics_objective.py, and now also
// build_predicted_timeseries_generic_pfr in generic_kinetics_objective.py
// for custom reactor_mode='pfr' fits — same shape, so no extra branch
// needed here). Fits with neither (saved before this change) just don't
// get an overlay — their row still works everywhere else (parameter
// table, R² table).
//
// PFR / plug-flow fits (the per-row-list shape, kind 'parity' below) don't
// share a time axis to overlay on, so they get their own layout instead of
// OverlayChartTimeseries: a parity plot (predicted vs. observed) per
// species, split into one graph per temperature (client feedback, 2-Sep —
// combining temperatures on one parity plot reads as noise), with a
// side-by-side/overlay toggle for how the selected fits sit within each
// temperature's graph. See groupPointsByTemperature and
// ParitySpeciesPanel below.

import React, { useEffect, useMemo, useState } from 'react';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import '../styles/DataFittingPage.css'; // shares the same --color-* design tokens
import { useFitRecords } from './useFitRecords';
import * as FitRecords from './fitRecordStore';

// API endpoint resolution without a separate configuration module.
// Local CRA development uses the Flask backend on port 5000.
// Production can provide REACT_APP_API_ORIGIN; otherwise same-origin /api is used.
const API_ORIGIN = (process.env.REACT_APP_API_ORIGIN ||
  ((typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
    ? 'http://localhost:5000'
    : (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000'))).replace(/\/$/, '');
const API_BASE = `${API_ORIGIN}/api`;


const cssVar = (name) => `var(${name})`;
const C = {
  accent: cssVar('--color-accent'), accentDim: cssVar('--color-accent-dim'),
  success: cssVar('--color-success'), error: cssVar('--color-error'),
  warningDark: cssVar('--color-warning-dark'),
  border: cssVar('--color-border'), borderLight: cssVar('--color-border-light'),
  text: cssVar('--color-text'), textMuted: cssVar('--color-text-muted'),
  textDim: cssVar('--color-text-dim'),
  card: cssVar('--color-card'), shell: cssVar('--color-shell'),
};
const font = { ui: cssVar('--font-ui') };

// One fixed color per selection slot (up to MAX_COMPARE=4 server-side) so a
// fit's color stays stable as other fits are checked/unchecked around it.
const FIT_COLORS = ['#4C7DF0', '#E0733F', '#3FAE6B', '#B24CD1'];

const r2Color = (r2) => (r2 == null ? C.textMuted : r2 > 0.95 ? C.success : r2 > 0.8 ? C.warningDark : C.error);
// R² label to pair with the number (client feedback, Fit Diagnostics call:
// R² can mathematically go negative when a fit is worse than predicting the
// mean — a bare "-2.97" reads as a confusing raw percentage, so every R²
// display needs this badge alongside it, not just a color). Same EXC/GOOD/
// POOR thresholds as DataFittingPage.jsx's Graph tab, kept in sync here.
const r2Label = (r2) => (r2 == null ? '—' : r2 > 0.95 ? 'EXC' : r2 > 0.8 ? 'GOOD' : 'POOR');

// Species names are loaded from the backend species_catalog; this file contains
// only lookup logic, never a second hard-coded chemical dictionary.
const toAsciiDigits = (str) => String(str || '').replace(/[₀-₉]/g, d =>
  String('₀₁₂₃₄₅₆₇₈₉'.indexOf(d))
);
const toSubscriptFormula = s => String(s).replace(/([A-Za-z])(\d+)/g, (_, l, d) =>
  l + d.split('').map(c => '₀₁₂₃₄₅₆₇₈₉'[c]).join(''));
// Field names match GET /api/species-catalog's response shape exactly
// (species_key / species_name / formula / cas_number / aliases) — the
// original version of this helper used id/name/cas, which never matched
// the catalog payload and silently fell back to the raw formula every time.
const speciesFieldValues = c => [
  c?.species_key, c?.species_name, c?.formula, c?.cas_number,
  ...(Array.isArray(c?.aliases) ? c.aliases : []),
].filter(Boolean).map(v => String(v).trim());
const speciesDisplayName = (species, catalog = []) => {
  const needle = toAsciiDigits(String(species ?? '').trim()).toLowerCase();
  const hit = catalog.find(c => speciesFieldValues(c).some(v =>
    toAsciiDigits(v).toLowerCase() === needle
  ));
  return hit?.species_name || toSubscriptFormula(species);
};

const Btn = ({ variant = 'primary', style, children, disabled, ...props }) => {
  const base = {
    fontFamily: font.ui, fontSize: 12, fontWeight: 600, borderRadius: 5,
    padding: '6px 12px', cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1, border: '1px solid transparent', transition: 'all .15s',
  };
  const variants = {
    primary: { background: C.accent, color: '#fff' },
    secondary: { background: 'transparent', color: C.accent, borderColor: C.accent },
    ghost: { background: 'transparent', color: C.textMuted, borderColor: C.borderLight },
    danger: { background: 'transparent', color: C.error, borderColor: C.error },
  };
  return <button {...props} disabled={disabled} style={{ ...base, ...variants[variant], ...style }}>{children}</button>;
};

const Badge = ({ color = C.accent, children }) => (
  <span style={{
    fontFamily: font.ui, fontSize: 10, fontWeight: 700, color, background: `${color}1a`,
    border: `1px solid ${color}55`, borderRadius: 3, padding: '1px 6px', letterSpacing: '0.03em',
  }}>{children}</span>
);

// ─── Export Selected — bundle charts + raw data + R² summary into one .zip ─
//
// Client ask (10-Sep): "Export Selected" should also carry the graphs, not
// just data. Charts here are hand-rolled <svg> (no charting lib), so rather
// than pull in a zip/canvas dependency for one button, this is a small
// self-contained ZIP writer (STORE method — no compression, keeps the code
// short and the export instant) plus a CSS-custom-property resolver, since
// a serialized SVG rasterized via <img>/canvas has no access to the page's
// :root stylesheet and would otherwise paint every "var(--color-x)" as
// black/invisible.
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    table[n] = c >>> 0;
  }
  return table;
})();
const crc32 = (bytes) => {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) crc = CRC_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
  return (crc ^ 0xFFFFFFFF) >>> 0;
};
const dosDateTime = (d = new Date()) => ({
  time: ((d.getHours() & 0x1F) << 11) | ((d.getMinutes() & 0x3F) << 5) | ((d.getSeconds() >> 1) & 0x1F),
  dosDate: (((d.getFullYear() - 1980) & 0x7F) << 9) | (((d.getMonth() + 1) & 0xF) << 5) | (d.getDate() & 0x1F),
});
// files: [{ name: 'charts/foo.png', data: Uint8Array }] -> zip Blob
function buildZip(files) {
  const enc = new TextEncoder();
  const { time, dosDate } = dosDateTime();
  const local = [], central = [];
  let offset = 0;
  files.forEach(({ name, data }) => {
    const nameBytes = enc.encode(name);
    const crc = crc32(data);
    const size = data.length;
    const lh = new DataView(new ArrayBuffer(30));
    lh.setUint32(0, 0x04034b50, true); lh.setUint16(4, 20, true); lh.setUint16(6, 0, true);
    lh.setUint16(8, 0, true); lh.setUint16(10, time, true); lh.setUint16(12, dosDate, true);
    lh.setUint32(14, crc, true); lh.setUint32(18, size, true); lh.setUint32(22, size, true);
    lh.setUint16(26, nameBytes.length, true); lh.setUint16(28, 0, true);
    local.push(new Uint8Array(lh.buffer), nameBytes, data);
    const ch = new DataView(new ArrayBuffer(46));
    ch.setUint32(0, 0x02014b50, true); ch.setUint16(4, 20, true); ch.setUint16(6, 20, true);
    ch.setUint16(8, 0, true); ch.setUint16(10, 0, true); ch.setUint16(12, time, true); ch.setUint16(14, dosDate, true);
    ch.setUint32(16, crc, true); ch.setUint32(20, size, true); ch.setUint32(24, size, true);
    ch.setUint16(28, nameBytes.length, true); ch.setUint16(30, 0, true); ch.setUint16(32, 0, true);
    ch.setUint16(34, 0, true); ch.setUint16(36, 0, true); ch.setUint32(38, 0, true); ch.setUint32(42, offset, true);
    central.push(new Uint8Array(ch.buffer), nameBytes);
    offset += 30 + nameBytes.length + size;
  });
  const centralSize = central.reduce((n, p) => n + p.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true); end.setUint16(8, files.length, true); end.setUint16(10, files.length, true);
  end.setUint32(12, centralSize, true); end.setUint32(16, offset, true);
  return new Blob([...local, ...central, new Uint8Array(end.buffer)], { type: 'application/zip' });
}
// Swap every literal "var(--color-x)" in a serialized SVG string for its
// resolved value — required before rasterizing, see comment above.
const resolveCssVarsInSvg = (svgString) => {
  const styles = getComputedStyle(document.documentElement);
  return svgString.replace(/var\((--[a-zA-Z0-9-]+)\)/g, (m, name) => styles.getPropertyValue(name).trim() || m);
};
// Rasterize one already-var-resolved SVG string to a PNG Blob. scale=2 for a
// reasonably crisp export out of a chart that's only ~320x190 on screen.
function svgStringToPngBlob(svgString, width, height, scale = 2) {
  return new Promise((resolve, reject) => {
    const bgColor = getComputedStyle(document.documentElement).getPropertyValue('--color-shell').trim() || '#0b1220';
    const url = URL.createObjectURL(new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' }));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width * scale; canvas.height = height * scale;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = bgColor; ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => { URL.revokeObjectURL(url); blob ? resolve(blob) : reject(new Error('toBlob failed')); }, 'image/png');
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}
// Filesystem-safe-ish filename fragment from a chart's species/temp/fit key.
const slug = (s) => String(s ?? 'unknown').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'unknown';

// ─── Normalize the two predicted_timeseries shapes into one common form ───
//
// Shape A (single time series — generic powerlaw/lhhw, MeOAc, DME):
//   { time_min: [...], species: { sp: { exp: [...], pred: [...] } } }
// Shape B (per-datapoint, multi-experiment — AA):
//   [ { row_index, species: { sp: { pred: [...], y0, exp_end } }, time_s: [...] }, ... ]
//
// Returns { kind: 'timeseries' | 'parity' | null, speciesList, forSpecies(sp) }
function normalizeSeries(predicted_timeseries) {
  if (!predicted_timeseries) return null;
  // Shape C (custom multi-Batch fit): an ARRAY OF DATA SETS, each with its own
  // time axis -> [ { dataset_label, time_min: [...], species: { sp: { exp, pred } } }, ... ].
  // This used to fall into the per-row 'parity' branch below, where every point
  // is dropped (there is no exp_end on these rows), so the species had NO
  // graph at all and only the tables rendered. Each data set is kept as its own
  // segment, because their time axes are independent and must not be joined.
  if (Array.isArray(predicted_timeseries) && predicted_timeseries.length
      && predicted_timeseries[0] && predicted_timeseries[0].time_min && predicted_timeseries[0].species) {
    const speciesList = Array.from(new Set(predicted_timeseries.flatMap(ds => Object.keys((ds && ds.species) || {}))));
    return {
      kind: 'timeseries',
      speciesList,
      forSpecies: (sp) => {
        const segments = predicted_timeseries.map((ds, i) => {
          const pair = ds && ds.species && ds.species[sp];
          if (!pair) return null;
          return {
            label: (ds && ds.dataset_label) || `Data Set ${i + 1}`,
            t: Array.isArray(ds.time_min) ? ds.time_min : [],
            exp: Array.isArray(pair.exp) ? pair.exp : [],
            pred: Array.isArray(pair.pred) ? pair.pred : [],
          };
        }).filter(Boolean);
        if (segments.length === 0) return null;
        return {
          t: segments.flatMap(g => g.t),
          exp: segments.flatMap(g => g.exp),
          pred: segments.flatMap(g => g.pred),
          segments,
        };
      },
    };
  }
  if (Array.isArray(predicted_timeseries)) {
    if (predicted_timeseries.length === 0) return null;
    const speciesList = Array.from(new Set(predicted_timeseries.flatMap(row => Object.keys(row.species || {}))));
    return {
      kind: 'parity',
      speciesList,
      // T_C carried per-point (present on every row from both
      // build_predicted_timeseries_aa and build_predicted_timeseries_generic_pfr)
      // so parity plots can be split by temperature — see
      // groupPointsByTemperature below. null for older saved fits from
      // before T_C was added to either builder; those fall into a single
      // "temperature not recorded" bucket rather than being dropped.
      forSpecies: (sp) => predicted_timeseries
        .filter(row => row.species && row.species[sp])
        .map(row => ({
          observed: row.species[sp].exp_end,
          predicted: Array.isArray(row.species[sp].pred) ? row.species[sp].pred[row.species[sp].pred.length - 1] : null,
          T_C: row.T_C != null ? row.T_C : null,
        }))
        .filter(pt => pt.observed != null && pt.predicted != null),
    };
  }
  if (predicted_timeseries.species) {
    const speciesList = Object.keys(predicted_timeseries.species);
    if (speciesList.length === 0) return null;
    return {
      kind: 'timeseries',
      speciesList,
      forSpecies: (sp) => {
        const s = predicted_timeseries.species[sp];
        if (!s) return null;
        // Accept the key names different run types use, and numbers that arrive as strings.
        const num = (arr) => (Array.isArray(arr) ? arr.map((v) => (v == null || v === '' ? null : Number(v))) : []);
        const exp = num(Array.isArray(s) ? s : (s.exp ?? s.experimental ?? s.y_exp));
        const pred = num(s.pred ?? s.predicted ?? s.fitted ?? s.y_pred);
        const rawT = predicted_timeseries.time_min ?? predicted_timeseries.time
          ?? predicted_timeseries.t ?? predicted_timeseries.time_h ?? predicted_timeseries.time_s;
        let t = num(rawT).filter((v) => Number.isFinite(v));
        const n = Math.max(exp.length, pred.length);
        // No usable time axis saved with the run -> plot against the data-point number
        // rather than dropping the graph.
        if (t.length === 0 || t.length < n) t = Array.from({ length: n }, (_, i) => i + 1);
        return { t, exp, pred };
      },
    };
  }
  return null;
}

// Where a saved fit's prediction series can live (newest -> oldest layouts).
function seriesOf(f) {
  const direct = f?.metrics?.predicted_timeseries
    ?? f?.metrics?.global_metrics?.predicted_timeseries
    ?? f?.plot_data?.predicted_timeseries
    ?? f?.predicted_timeseries;
  if (direct) return direct;
  const yExp = f?.metrics?.y_i_exp;            // older endpoints: per-species arrays, no time axis
  if (yExp && typeof yExp === 'object') {
    const yPred = f.metrics.y_i_pred || {};
    return { species: Object.fromEntries(Object.keys(yExp).map((sp) => [sp, { exp: yExp[sp], pred: yPred[sp] || [] }])) };
  }
  return null;
}

// Plain-language reason a fit has no graph (shown under the overlay heading).
function whyNoSeries(f) {
  const raw = seriesOf(f);
  if (!raw) return 'no prediction series was saved with this run';
  const keys = Array.isArray(raw)
    ? `list of ${raw.length}${raw[0] ? ` (${Object.keys(raw[0]).slice(0, 5).join(', ')})` : ''}`
    : Object.keys(raw).slice(0, 6).join(', ');
  return `a series was saved but is empty or in a layout this page doesn't recognise (${keys})`;
}

// ─── Split a parity-kind species' per-fit points by temperature ───────────
//
// Client feedback (Compare Fits review, 2-Sep): combining multiple
// temperatures on one PFR parity plot is visually confusing — each
// distinct temperature present across the selected fits' points should
// get its own graph. Groups on the rounded T_C value (build_predicted_
// timeseries_aa / build_predicted_timeseries_generic_pfr already round to
// 1 decimal), so two runs nominally "at 250°C" land in the same group even
// with tiny float noise. Points with no T_C (older saved fits, or the
// generic BATCH/single-timeseries codepath, which never reaches this
// helper) fall into one "temperature not recorded" bucket rather than
// being silently dropped.
function groupPointsByTemperature(fitsWithSeries) {
  const keyOf = (t) => (t == null ? '\u2014' : t.toFixed(1));
  const groups = new Map(); // key -> { T_C, entries: [{label, color, series}] }
  fitsWithSeries.forEach(({ label, color, series }) => {
    const byKey = new Map();
    series.forEach((pt) => {
      const k = keyOf(pt.T_C);
      if (!byKey.has(k)) byKey.set(k, []);
      byKey.get(k).push(pt);
    });
    byKey.forEach((pts, k) => {
      if (!groups.has(k)) groups.set(k, { T_C: pts[0].T_C, entries: [] });
      groups.get(k).entries.push({ label, color, series: pts });
    });
  });
  return Array.from(groups.values()).sort(
    (a, b) => (a.T_C ?? -Infinity) - (b.T_C ?? -Infinity)
  );
}

// ─── One small multi-series SVG chart for a single species ────────────────
const W = 320, H = 190, PAD = { l: 38, r: 10, t: 10, b: 26 };

const OverlayChartTimeseries = ({ species, fitsWithSeries, chartKey }) => {
  // fitsWithSeries: [{ label, color, series: { t, exp, pred } }]
  const allX = fitsWithSeries.flatMap(f => f.series.t).filter(Number.isFinite);
  const allY = fitsWithSeries.flatMap(f => [...f.series.exp, ...f.series.pred]).filter(Number.isFinite);
  if (allX.length === 0 || allY.length === 0) return null;
  const xMin = 0, xMax = Math.max(...allX, 1e-9);
  const yMin = Math.min(0, ...allY), yMax = Math.max(...allY, 1e-9) * 1.08;
  const sx = (v) => PAD.l + ((v - xMin) / (xMax - xMin || 1)) * (W - PAD.l - PAD.r);
  const sy = (v) => H - PAD.b - ((v - yMin) / (yMax - yMin || 1)) * (H - PAD.t - PAD.b);

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}
      data-chart-key={chartKey} data-chart-w={W} data-chart-h={H}>
      <rect x={PAD.l} y={PAD.t} width={W - PAD.l - PAD.r} height={H - PAD.t - PAD.b}
        fill="none" stroke={C.borderLight} strokeWidth="1" />
      {/* y ticks */}
      {[0, 0.5, 1].map((f) => {
        const v = yMin + f * (yMax - yMin);
        return (
          <text key={f} x={PAD.l - 5} y={sy(v)} textAnchor="end" dominantBaseline="middle"
            fontSize="8" fontFamily={font.ui} fill={C.textDim}>{v.toFixed(2)}</text>
        );
      })}
      <text x={PAD.l} y={H - 4} fontSize="8" fontFamily={font.ui} fill={C.textDim}>0</text>
      <text x={W - PAD.r} y={H - 4} textAnchor="end" fontSize="8" fontFamily={font.ui} fill={C.textDim}>
        {xMax.toFixed(1)} min
      </text>

      {fitsWithSeries.map(({ color, series }, idx) => {
        // A multi-data-set fit carries one segment per data set (independent time
        // axes, drawn as separate curves with a different dash); every other fit is one segment.
        const segs = series.segments || [{ t: series.t, exp: series.exp, pred: series.pred }];
        const DASH = [undefined, '6 3', '2 2'];
        return (
          <g key={idx}>
            {segs.map((seg, si) => {
              const linePts = seg.t
                .map((t, i) => (Number.isFinite(t) && Number.isFinite(seg.pred[i]) ? `${sx(t)},${sy(seg.pred[i])}` : null))
                .filter(Boolean).join(' ');
              return (
                <g key={si}>
                  {/* Data — this fit's own experimental points (kept per-fit rather
                      than deduped across fits, since two fits may have been run
                      against different selected datasets). */}
                  {seg.t.map((t, i) => Number.isFinite(t) && Number.isFinite(seg.exp[i]) && (
                    <circle key={i} cx={sx(t)} cy={sy(seg.exp[i])} r="2.4" fill={color} fillOpacity="0.35" />
                  ))}
                  {/* Predicted curve */}
                  <polyline points={linePts} fill="none" stroke={color} strokeWidth="1.8"
                    strokeDasharray={DASH[si % DASH.length]} />
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
};

const OverlayChartParity = ({ species, fitsWithSeries, chartKey }) => {
  // fitsWithSeries: [{ label, color, series: [{observed, predicted}, ...] }]
  const allVals = fitsWithSeries.flatMap(f => f.series.flatMap(p => [p.observed, p.predicted]));
  if (allVals.length === 0) return null;
  const vMin = Math.min(0, ...allVals), vMax = Math.max(...allVals, 1e-9) * 1.08;
  const s = (v) => PAD.l + ((v - vMin) / (vMax - vMin || 1)) * (W - PAD.l - PAD.r);
  const sy = (v) => H - PAD.b - ((v - vMin) / (vMax - vMin || 1)) * (H - PAD.t - PAD.b);
  // 4 sig figs is plenty for an axis tick; avoids "0.123456789"-style overflow.
  const fmt = (v) => (Math.abs(v) >= 100 ? v.toFixed(0) : Math.abs(v) >= 1 ? v.toFixed(2) : v.toPrecision(2));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => vMin + f * (vMax - vMin));

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}
      data-chart-key={chartKey} data-chart-w={W} data-chart-h={H}>
      <rect x={PAD.l} y={PAD.t} width={W - PAD.l - PAD.r} height={H - PAD.t - PAD.b}
        fill="none" stroke={C.borderLight} strokeWidth="1" />
      {/* y = x reference line — a perfect fit lands every point on this */}
      <line x1={s(vMin)} y1={sy(vMin)} x2={s(vMax)} y2={sy(vMax)}
        stroke={C.textDim} strokeWidth="1" strokeDasharray="3,3" />

      {/* Numeric scale — SAME units on both axes (this is a parity plot, not
          two independent quantities), auto-scaled to THIS species' own
          observed+predicted range. Different species panels are NOT on a
          shared scale — read absolute numbers within one panel, and
          closeness-to-diagonal (not raw position) when comparing panels. */}
      {ticks.map((v, i) => (
        <g key={i}>
          <line x1={s(v)} y1={H - PAD.b} x2={s(v)} y2={H - PAD.b + 3} stroke={C.textDim} strokeWidth="1" />
          <text x={s(v)} y={H - PAD.b + 12} textAnchor="middle" fontSize="7" fontFamily={font.ui} fill={C.textDim}>
            {fmt(v)}
          </text>
          <line x1={PAD.l - 3} y1={sy(v)} x2={PAD.l} y2={sy(v)} stroke={C.textDim} strokeWidth="1" />
          <text x={PAD.l - 5} y={sy(v)} textAnchor="end" dominantBaseline="middle" fontSize="7" fontFamily={font.ui} fill={C.textDim}>
            {fmt(v)}
          </text>
        </g>
      ))}
      <text x={(PAD.l + W - PAD.r) / 2} y={H - 2} textAnchor="middle" fontSize="8" fontFamily={font.ui} fill={C.textDim}>
        Measured
      </text>
      <text x={9} y={(PAD.t + H - PAD.b) / 2} textAnchor="middle" fontSize="8" fontFamily={font.ui} fill={C.textDim}
        transform={`rotate(-90, 9, ${(PAD.t + H - PAD.b) / 2})`}>
        Predicted
      </text>

      {fitsWithSeries.map(({ color, series }, idx) => (
        <g key={idx}>
          {series.map((pt, i) => (
            <circle key={i} cx={s(pt.observed)} cy={sy(pt.predicted)} r="3" fill={color} fillOpacity="0.75" />
          ))}
        </g>
      ))}
    </svg>
  );
};

// ─── One species' PFR/parity comparison: one graph per temperature,       ─
//     each either all fits overlaid (colored) or one small chart per fit
//     side by side. Both are useful (Madhura, 2-Sep); side-by-side is the
//     default per her "preferred as a first step."
const ParitySpeciesPanel = ({ species, entries, mode }) => {
  const groups = useMemo(() => groupPointsByTemperature(entries), [entries]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {groups.map((g) => (
        <div key={g.T_C ?? 'unknown'}>
          <div style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, marginBottom: 4, fontFamily: font.ui }}>
            {g.T_C != null ? `T \u2248 ${g.T_C}\u00B0C` : 'Temperature not recorded'}
          </div>
          {mode === 'overlay' ? (
            <OverlayChartParity species={species} fitsWithSeries={g.entries}
              chartKey={`${species}|T${g.T_C ?? 'na'}|overlay`} />
          ) : (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {g.entries.map((entry, i) => (
                <div key={i}>
                  <div style={{ fontSize: 10, color: entry.color, fontWeight: 700, marginBottom: 2, fontFamily: font.ui }}>
                    {entry.label}
                  </div>
                  <OverlayChartParity species={species} fitsWithSeries={[entry]}
                    chartKey={`${species}|T${g.T_C ?? 'na'}|${entry.label}`} />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Compare results — parameter table + per-species R² + overlay ─────────
const CompareResultsView = ({ compareResult, registeredComponents = [], registerExport }) => {
  // Full species names: prefer the per-fit component_names the backend
  // already returns in each fit's metrics (populated from that fit's own
  // Registered Components at save time), then fall back to the live
  // species_catalog lookup for anything none of the selected fits recorded.
  const fitComponentNames = useMemo(() => {
    const out = {};
    (compareResult?.fits || []).forEach(f => {
      Object.entries(f.metrics?.component_names || {}).forEach(([key, name]) => {
        if (name && !out[key]) out[key] = name;
      });
    });
    return out;
  }, [compareResult]);
  const nameOf = React.useCallback(
    (sp) => fitComponentNames[sp] || fitComponentNames[String(sp).toUpperCase()]
      || speciesDisplayName(sp, registeredComponents),
    [fitComponentNames, registeredComponents]
  );

  const overlaysBySpecies = useMemo(() => {
    const bySpecies = {};
    compareResult.fits.forEach((f, idx) => {
      const norm = normalizeSeries(seriesOf(f));
      if (!norm) return;
      norm.speciesList.forEach((sp) => {
        const series = norm.forSpecies(sp);
        const hasData = Array.isArray(series) ? series.length > 0 : Boolean(series && series.t && series.t.length);
        if (!hasData) return;   // nothing plottable for this species in this fit
        if (!bySpecies[sp]) bySpecies[sp] = { kind: norm.kind, entries: [] };
        bySpecies[sp].entries.push({ label: f.label, color: FIT_COLORS[idx % FIT_COLORS.length], series });
      });
    });
    return bySpecies;
  }, [compareResult]);

  const speciesWithOverlay = Object.keys(overlaysBySpecies);
  // Batch (vs.-time) species overlay fine on one shared time axis — PFR/
  // plug-flow (parity) species don't have one, so they get their own
  // per-temperature layout below (ParitySpeciesPanel) instead of
  // OverlayChartTimeseries. A comparison can mix both kinds if the
  // selected fits themselves mix batch and PFR runs.
  const hasParitySpecies = speciesWithOverlay.some((sp) => overlaysBySpecies[sp].kind === 'parity');

  // PFR parity plots: side-by-side (one small chart per fit) is the
  // client's preferred default; overlay (all fits on one chart, distinct
  // colors) stays available as a toggle — Madhura, 2-Sep: "both options
  // considered useful, side-by-side preferred as a first step."
  const [parityMode, setParityMode] = useState('side-by-side');

  // Component tabs (Madhura, PFR prototype feedback, clarified 10-Sep:
  // "wants side by side showcase of the graphs for comparison purpose").
  // First cut of this made tabs show ONE species at a time, which lost
  // the at-a-glance side-by-side view Madhura actually wants. Matches the
  // existing Graph page pattern instead (Composition Profile Overview +
  // one tab per species — see DataFittingPage.jsx's Graph tab, which
  // already does exactly this for a single run): an "Overview" tab shows
  // every species' chart side by side in a grid, and individual species
  // tabs are still there for zooming into one at a time. Overview is the
  // default so nothing is hidden behind a click by default; switching
  // fits/compareResult resets to Overview unless the user is sitting on a
  // still-valid individual species tab.
  const OVERVIEW_TAB = '__overview__';
  const [activeTab, setActiveTab] = useState(OVERVIEW_TAB);
  useEffect(() => {
    if (activeTab !== OVERVIEW_TAB && !speciesWithOverlay.includes(activeTab)) {
      setActiveTab(OVERVIEW_TAB);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speciesWithOverlay.join('|')]);

  // Charts are only ALL mounted at once on the Overview tab (a single
  // species tab only renders that one species) — the export needs every
  // chart, so it forces Overview on, waits a frame for it to paint, walks
  // the DOM for every svg[data-chart-key] tagged above, then restores
  // whatever tab the user was actually on.
  const chartsContainerRef = React.useRef(null);
  const handleExportAll = React.useCallback(async () => {
    const previousTab = activeTab;
    if (previousTab !== OVERVIEW_TAB) setActiveTab(OVERVIEW_TAB);
    // Two rAFs: one for React to commit the tab switch, one for the browser to paint it.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    const files = [];
    const svgEls = chartsContainerRef.current
      ? Array.from(chartsContainerRef.current.querySelectorAll('svg[data-chart-key]'))
      : [];
    for (const svgEl of svgEls) {
      const key = svgEl.getAttribute('data-chart-key') || 'chart';
      const w = Number(svgEl.getAttribute('data-chart-w')) || svgEl.clientWidth || 320;
      const h = Number(svgEl.getAttribute('data-chart-h')) || svgEl.clientHeight || 190;
      const clone = svgEl.cloneNode(true);
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      const svgString = resolveCssVarsInSvg(new XMLSerializer().serializeToString(clone));
      const baseName = `charts/${key.split('|').map(slug).join('__')}`;
      files.push({ name: `${baseName}.svg`, data: new TextEncoder().encode(svgString) });
      try {
        const pngBlob = await svgStringToPngBlob(svgString, w, h, 2);
        files.push({ name: `${baseName}.png`, data: new Uint8Array(await pngBlob.arrayBuffer()) });
      } catch (e) {
        // A single chart failing to rasterize shouldn't block the rest of the export.
        console.error('Chart PNG export failed for', key, e);
      }
    }

    // Combined CSV of every point behind every chart — one file covering
    // both timeseries (vs. time) and parity (steady-state) species/fits.
    const rows = [['fit_label', 'species', 'series_kind', 'temperature_C', 'time_min', 'observed', 'predicted']];
    Object.entries(overlaysBySpecies).forEach(([sp, { kind, entries }]) => {
      entries.forEach(({ label, series }) => {
        if (kind === 'timeseries') {
          series.t.forEach((t, i) => rows.push([label, nameOf(sp), 'timeseries', '', t, series.exp[i] ?? '', series.pred[i] ?? '']));
        } else {
          series.forEach((pt) => rows.push([label, nameOf(sp), 'parity', pt.T_C ?? '', '', pt.observed, pt.predicted]));
        }
      });
    });
    const csvEscape = (v) => (typeof v === 'string' && /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    const rawPointsCsv = rows.map((r) => r.map(csvEscape).join(',')).join('\n');
    files.push({ name: 'data/raw_points.csv', data: new TextEncoder().encode(rawPointsCsv) });

    // R² summary — same numbers as the on-screen table, plus each fit's
    // overall R² where the backend returned one.
    const r2Rows = [['species', ...compareResult.fits.map((f) => f.label)]];
    Object.entries(compareResult.per_species_r2_table || {}).forEach(([sp, byFit]) => {
      r2Rows.push([nameOf(sp), ...compareResult.fits.map((f) => (byFit[f.fit_uuid] != null ? byFit[f.fit_uuid].toFixed(4) : ''))]);
    });
    if (compareResult.fits.some((f) => f.overall_r2 != null)) {
      r2Rows.push(['overall_r2', ...compareResult.fits.map((f) => (f.overall_r2 != null ? f.overall_r2.toFixed(4) : ''))]);
    }
    const r2Csv = r2Rows.map((r) => r.map(csvEscape).join(',')).join('\n');
    files.push({ name: 'data/r2_summary.csv', data: new TextEncoder().encode(r2Csv) });

    const zipBlob = buildZip(files);
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compare-fits-export-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);

    if (previousTab !== OVERVIEW_TAB) setActiveTab(previousTab);
  }, [activeTab, overlaysBySpecies, compareResult, nameOf]);

  useEffect(() => {
    registerExport?.(handleExportAll);
    return () => registerExport?.(null);
  }, [handleExportAll, registerExport]);

  return (
    <div ref={chartsContainerRef} style={{ border: `1px solid ${C.borderLight}`, borderRadius: 5, padding: 12, background: C.shell }}>
      {/* ── Graphical overlay — the new piece Madhura asked for ── */}
      <div style={{ fontSize: 12, fontWeight: 700, color: C.textDim, marginBottom: 4,
        textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: font.ui }}>
        Graphical Overlay vs. Data
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 11,
          fontFamily: font.ui, color: C.textMuted }}>
          {compareResult.fits.map((f, idx) => (
            <span key={f.fit_uuid} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%',
                background: FIT_COLORS[idx % FIT_COLORS.length], display: 'inline-block' }} />
              {f.label}
            </span>
          ))}
        </div>
        {hasParitySpecies && (
          <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
            {[['side-by-side', 'Side by side'], ['overlay', 'Overlay']].map(([m, text]) => (
              <Btn key={m} variant={parityMode === m ? 'primary' : 'ghost'}
                style={{ fontSize: 10, padding: '3px 8px' }}
                onClick={() => setParityMode(m)}>
                {text}
              </Btn>
            ))}
          </div>
        )}
      </div>
      {speciesWithOverlay.length === 0 ? (
        <div style={{ fontSize: 12, color: C.textDim, fontFamily: font.ui, marginBottom: 14 }}>
          No plottable prediction data on these fits (they were saved before the overlay
          was added, or the run type doesn't export a plottable series). The tables below
          still work.
          <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
            {compareResult.fits.map((f) => (
              <li key={f.fit_uuid}><strong>{f.label}</strong>: {whyNoSeries(f)}</li>
            ))}
          </ul>
        </div>
      ) : (
        <>
          {/* Tabs: Overview (all species side by side) + one per species.
              Tab order follows overlaysBySpecies' natural
              (first-seen-across-fits) order rather than being re-sorted,
              so it stays stable as fits are added/removed from the
              selection. */}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10,
            borderBottom: `1px solid ${C.borderLight}`, paddingBottom: 8 }}>
            {[[OVERVIEW_TAB, 'Overview'], ...speciesWithOverlay.map((sp) => [sp, nameOf(sp)])].map(([key, text]) => {
              const isActive = key === activeTab;
              return (
                <button key={key} onClick={() => setActiveTab(key)} style={{
                  fontFamily: font.ui, fontSize: 12, fontWeight: 600,
                  padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
                  border: `1px solid ${isActive ? C.accent : C.borderLight}`,
                  background: isActive ? C.accentDim : 'transparent',
                  color: isActive ? C.accent : C.textMuted,
                }}>
                  {text}
                </button>
              );
            })}
          </div>

          {activeTab === OVERVIEW_TAB ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginBottom: 16 }}>
              {speciesWithOverlay.map((sp) => {
                const { kind, entries } = overlaysBySpecies[sp];
                return (
                  <div key={sp}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.text, marginBottom: 3, fontFamily: font.ui }}>{nameOf(sp)}</div>
                    {kind === 'parity'
                      ? <ParitySpeciesPanel species={sp} entries={entries} mode={parityMode} />
                      : <OverlayChartTimeseries species={sp} fitsWithSeries={entries} chartKey={`${sp}|timeseries|all`} />}
                  </div>
                );
              })}
            </div>
          ) : overlaysBySpecies[activeTab] && (() => {
            const { kind, entries } = overlaysBySpecies[activeTab];
            return (
              <div style={{ marginBottom: 16 }}>
                {kind === 'parity'
                  ? <ParitySpeciesPanel species={activeTab} entries={entries} mode={parityMode} />
                  : <OverlayChartTimeseries species={activeTab} fitsWithSeries={entries} chartKey={`${activeTab}|timeseries|all`} />}
              </div>
            );
          })()}
        </>
      )}

      {/* ── Summary — what each run represents ──
          [FIX 19-Sep-2026] This whole table (Model / Reversibility /
          Exponents / Overall R² rows, one column per selected fit) existed
          before the Graphical Overlay feature above was added, then never
          made it into CompareResultsView when that feature landed — this
          file only ever had the bare "Per-Species R²" table below it. Not
          a styling regression, a missing block. Restored here, folded
          together with the per-species R² rows into one table (matching
          how it looked before) instead of two separate tables. Column
          headers are colored per FIT_COLORS so they line up with the
          Graphical Overlay legend and the checklist dots above. */}
      <div style={{ fontSize: 12, fontWeight: 700, color: C.textDim, marginBottom: 8,
        textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: font.ui }}>
        Summary — What Each Run Represents
      </div>
      <table style={{ width: '100%', fontSize: 12, fontFamily: font.ui, borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '3px 6px' }} />
            {compareResult.fits.map((f, idx) => (
              <th key={f.fit_uuid} style={{ textAlign: 'right', padding: '3px 6px', fontWeight: 700,
                color: FIT_COLORS[idx % FIT_COLORS.length] }}>
                {f.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ padding: '3px 6px', color: C.textMuted }}>Model</td>
            {compareResult.fits.map((f) => (
              <td key={f.fit_uuid} style={{ textAlign: 'right', padding: '3px 6px', color: C.text }}>
                {f.model_type === 'lhhw' ? 'LHHW' : 'Power Law'}
              </td>
            ))}
          </tr>
          <tr>
            <td style={{ padding: '3px 6px', color: C.textMuted }}>Reversibility</td>
            {compareResult.fits.map((f) => (
              <td key={f.fit_uuid} style={{ textAlign: 'right', padding: '3px 6px', color: C.text }}>
                {f.reversibility === 'reversible' ? 'Reversible'
                  : f.reversibility === 'irreversible' ? 'Irreversible'
                  : (f.reversibility || '—')}
              </td>
            ))}
          </tr>
          <tr>
            <td style={{ padding: '3px 6px', color: C.textMuted }}>Exponents</td>
            {compareResult.fits.map((f) => (
              <td key={f.fit_uuid} style={{ textAlign: 'right', padding: '3px 6px', color: C.text }}>
                {f.exponent_mode === 'fixed_1' ? 'Fixed at 1'
                  : f.exponent_mode === 'regressed' ? 'Regressed'
                  : (f.exponent_mode || '—')}
              </td>
            ))}
          </tr>
          <tr>
            <td style={{ padding: '3px 6px', color: C.textMuted, fontWeight: 700 }}>Overall R²</td>
            {compareResult.fits.map((f) => (
              <td key={f.fit_uuid} style={{ textAlign: 'right', padding: '3px 6px' }}>
                {f.overall_r2 != null ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', justifyContent: 'flex-end' }}>
                    <span style={{ color: r2Color(f.overall_r2), fontWeight: 700 }}>{f.overall_r2.toFixed(4)}</span>
                    <Badge color={r2Color(f.overall_r2)}>{r2Label(f.overall_r2)}</Badge>
                  </span>
                ) : (
                  <span style={{ color: C.textMuted }}>—</span>
                )}
              </td>
            ))}
          </tr>
          {Object.entries(compareResult.per_species_r2_table || {}).map(([sp, byFit]) => (
            <tr key={sp}>
              <td style={{ padding: '3px 6px', color: C.textMuted }}>R² — {nameOf(sp)}</td>
              {compareResult.fits.map((f) => {
                const v = byFit[f.fit_uuid];
                return (
                  <td key={f.fit_uuid} style={{ textAlign: 'right', padding: '3px 6px' }}>
                    {v != null ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', justifyContent: 'flex-end' }}>
                        <span style={{ color: r2Color(v), fontWeight: 700 }}>{v.toFixed(4)}</span>
                        <Badge color={r2Color(v)}>{r2Label(v)}</Badge>
                      </span>
                    ) : (
                      <span style={{ color: C.textMuted }}>—</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
          <tr>
            <td style={{ padding: '3px 6px', color: C.textMuted }}>Status</td>
            {compareResult.fits.map((f) => (
              <td key={f.fit_uuid} style={{ textAlign: 'right', padding: '3px 6px' }}>
                {f.status === 'converged' ? (
                  <span style={{ color: C.success }}>Converged</span>
                ) : f.status === 'failed' ? (
                  <span style={{ color: C.error }}>Failed</span>
                ) : (
                  <span style={{ color: C.text }}>{f.status || '—'}</span>
                )}
              </td>
            ))}
          </tr>
          <tr>
            <td style={{ padding: '3px 6px', color: C.textMuted }}>Saved</td>
            {compareResult.fits.map((f) => {
              const ts = f.saved_at || f.created_at || f.updated_at;
              return (
                <td key={f.fit_uuid} style={{ textAlign: 'right', padding: '3px 6px', color: C.text, whiteSpace: 'nowrap' }}>
                  {ts ? new Date(ts).toLocaleString() : '—'}
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={{ padding: '3px 6px', color: C.textMuted }}>Active fit</td>
            {compareResult.fits.map((f) => (
              <td key={f.fit_uuid} style={{ textAlign: 'right', padding: '3px 6px' }}>
                {f.is_active ? <Badge color={C.success}>ACTIVE</Badge> : <span style={{ color: C.textMuted }}>—</span>}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      <div style={{ fontSize: 11, color: C.textDim, fontFamily: font.ui, marginTop: 6, fontStyle: 'italic' }}>
        Raw fitted parameters aren't shown here — visual fit-to-data comparison above is what drives model selection.
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
const CompareFitsPage = () => {
  const { fits: systemFits, allFits, selectedFitUuids, compareResult, loading, error, reactionSystemName } = useFitRecords();

  // BUG FIX (Reports -> "Compare selected" landed on "No saved fits yet"):
  // `fits` only holds the CURRENT reaction system's runs, but Reports lists runs
  // from ALL of the user's systems. A run picked there belongs to another system,
  // so this page's list was empty and the selection + graphs were hidden.
  // The list is now the user-wide runs merged with the current system's runs.
  const fits = useMemo(() => {
    const byUuid = new Map();
    [...(allFits || []), ...(systemFits || [])].forEach((f) => {
      byUuid.set(f.fit_uuid, { ...(byUuid.get(f.fit_uuid) || {}), ...f });
    });
    return Array.from(byUuid.values());
  }, [allFits, systemFits]);

  // Fits from different reaction systems don't share species/conditions, so
  // overlaying them is not a like-for-like comparison — warn instead of silently mixing.
  const selectedSystems = useMemo(() => {
    const names = new Set();
    fits.forEach((f) => {
      if (selectedFitUuids.includes(f.fit_uuid)) names.add(f.system_uuid || f.system_name || 'current');
    });
    return names;
  }, [fits, selectedFitUuids]);
  const [bootstrapped, setBootstrapped] = useState(false);
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  // Populated by CompareResultsView once it has charts+data to export (see
  // CompareResultsView's registerExport). Stays null until a compare has
  // actually been run, so the button below falls back to the plain data-only
  // export in that case rather than doing nothing.
  const exportAllRef = React.useRef(null);

  // BUG FIX (Shankar, 25-Aug): with no token, the bootstrap effect below
  // just skipped itself (`if (!token) return;`) and left the page sitting
  // there with an empty "No saved fits yet" state — no indication anything
  // was wrong, no path back to login. Redirect straight there instead.
  //
  // BUG FIX (eslint react-hooks/rules-of-hooks, 25-Aug): this used to
  // `return null` right here, between this effect and the bootstrap effect
  // below — so on a no-token render, the second `useEffect` simply never
  // ran, violating the Rules of Hooks (hook count must match every
  // render). The redirect effect stays here; the "don't render" half moved
  // to a plain `if` right before the final `return (` below, after every
  // hook in this component has unconditionally run.
  useEffect(() => {
    if (!token) window.location.href = '/';
  }, [token]);

  // A user can land on /compare-fits directly (not via Data Fitting first),
  // so this page bootstraps the same project/reaction-system resolution
  // DataFittingPage does — with no signature, which resolves to whatever
  // reaction system is already current for this account without creating
  // a new one (see fit_records_api.py's get_default_reaction_system()).
  // On an expired (rather than missing) token, resolveDefaultProjectId's
  // 401 handling in fitRecordStore.js takes over from here — it redirects
  // itself, so nothing further needs handling in this effect.
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const projectId = await FitRecords.resolveDefaultProjectId();
        if (projectId) {
          const systemId = await FitRecords.ensureReactionSystem(projectId);
          if (systemId) await FitRecords.refreshFitRecords();
        }
        await FitRecords.refreshAllFitRecords();   // runs from every reaction system
      } finally {
        setBootstrapped(true);
      }
    })();
  }, [token]);

  // BUG FIX (summary showed fits that were no longer ticked): the comparison
  // result was only refreshed when "Compare Selected" was clicked, so after
  // changing the ticks the page kept showing the PREVIOUS fits' result under the
  // NEW selection. The result now follows the selection: whenever the ticked fits
  // differ from the fits in the shown result, it is recomputed automatically
  // (this also covers arriving from Reports with fits already ticked).
  const wantedKey = [...selectedFitUuids].sort().join('|');
  const shownKey = (compareResult?.fits || []).map((f) => f.fit_uuid).sort().join('|');
  const resultIsCurrent = Boolean(compareResult) && shownKey === wantedKey;
  useEffect(() => {
    if (!bootstrapped || selectedFitUuids.length < 1 || resultIsCurrent) return undefined;
    const timer = setTimeout(() => FitRecords.runCompare(), 350);   // debounce rapid ticking
    return () => clearTimeout(timer);
  }, [bootstrapped, wantedKey, resultIsCurrent]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Full species names for the Overlay/R² views come primarily from each
  // saved fit's own component_names (see fitComponentNames in
  // CompareResultsView), but that only covers species a fit's *original*
  // form had Registered Components for. This fetches the live
  // species_catalog as a fallback so any other known species still gets its
  // real name instead of a bare formula.
  const [speciesCatalog, setSpeciesCatalog] = useState([]);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/species-catalog?limit=5000`, {
          headers: { Authorization: token },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setSpeciesCatalog(data.species || []);
      } catch (e) {
        console.error('Failed to load species catalog for Compare Fits:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // See the "BUG FIX (eslint react-hooks/rules-of-hooks...)" comment above,
  // next to `token` — render-time half of that fix; both hooks above have
  // now run unconditionally on every render, so this doesn't change hook
  // count between renders.
  if (!token) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden' }}>
      <Sidebar />
      <div style={{ marginLeft: 'var(--sidebar-w)', width: 'calc(100% - var(--sidebar-w))',
        display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Navbar activePage="app" />
        <div style={{ flex: 1, marginTop: 68, overflowY: 'auto', padding: 20, background: C.shell }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
              <h1 style={{ fontFamily: font.ui, fontSize: 20, fontWeight: 700, color: C.text, margin: 0 }}>Compare Fits</h1>
              {reactionSystemName && (
                <span style={{ fontFamily: font.ui, fontSize: 12, color: C.textMuted }}>{reactionSystemName}</span>
              )}
            </div>
            <p style={{ fontFamily: font.ui, fontSize: 13, color: C.textDim, marginTop: 0, marginBottom: 16 }}>
              {/* Copy fix (2-Sep gap): this used to say "select two or more,"
                  left over from before the compare gate was lowered to 1+ (see
                  the comment on the Compare button below) — a single
                  custom-system fit renders its own parity plot fine, and this
                  text was telling people otherwise. */}
              Select one or more saved fits to view their predicted curves against the experimental data
              — select two or more to overlay them and compare parameters and per-species R² side by side.
            </p>

            {error && (
              <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 5, fontSize: 12, fontFamily: font.ui,
                background: `${C.warningDark}18`, border: `1px solid ${C.warningDark}55`, color: C.warningDark }}>
                {error}
              </div>
            )}

            {fits.length === 0 && !bootstrapped ? (
              <div style={{ fontSize: 13, color: C.textDim, fontFamily: font.ui, padding: '24px 4px' }}>Loading saved fits…</div>
            ) : fits.length === 0 ? (
              <div style={{ fontSize: 13, color: C.textDim, fontFamily: font.ui, padding: '24px 4px' }}>
                No saved fits yet. Go to Data Fitting → Optimize, run a fit, and use "Save this fit" —
                run it again with different settings and save that too, to have something to compare here.
              </div>
            ) : (
              <>
                {selectedSystems.size > 1 && (
                  <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 5, fontSize: 12, fontFamily: font.ui,
                    background: `${C.warningDark}18`, border: `1px solid ${C.warningDark}55`, color: C.warningDark }}>
                    The selected fits come from different reaction systems, so their curves and parameters are not
                    directly comparable. Select fits from one reaction system for a like-for-like comparison.
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
                  {fits.map((f) => {
                    const checked = selectedFitUuids.includes(f.fit_uuid);
                    const disabled = !checked && selectedFitUuids.length >= 4;
                    return (
                      <label key={f.fit_uuid} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                        borderRadius: 5, cursor: disabled ? 'not-allowed' : 'pointer',
                        background: checked ? C.accentDim : C.card,
                        border: `1px solid ${checked ? C.accent + '44' : C.borderLight}`,
                        fontSize: 12, fontFamily: font.ui, opacity: disabled ? 0.5 : 1,
                      }}>
                        <input type="checkbox" checked={checked} disabled={disabled}
                          onChange={() => FitRecords.toggleFitSelected(f.fit_uuid)}
                          style={{ accentColor: C.accent }} />
                        <span style={{ flex: 1, color: C.text }}>{f.label}</span>
                        {f.system_name && <span style={{ color: C.textDim, fontSize: 11 }}>{f.system_name}</span>}
                        {f.is_active && <Badge color={C.success}>ACTIVE</Badge>}
                        <Badge color={f.model_type === 'lhhw' ? C.accent : C.textMuted}>
                          {f.model_type === 'lhhw' ? 'LHHW' : 'POWER LAW'}
                        </Badge>
                        {f.overall_r2 != null && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ color: r2Color(f.overall_r2), fontWeight: 700 }}>
                              R² {f.overall_r2.toFixed(3)}
                            </span>
                            <Badge color={r2Color(f.overall_r2)}>{r2Label(f.overall_r2)}</Badge>
                          </span>
                        )}
                        <Btn variant="ghost" style={{ fontSize: 11, padding: '2px 6px' }}
                          onClick={(e) => { e.preventDefault(); FitRecords.setActiveFit(f.fit_uuid); }}
                          title="Feeds Appendix / export / reports">
                          Set Active
                        </Btn>
                        <Btn variant="danger" style={{ fontSize: 11, padding: '2px 6px' }}
                          onClick={(e) => { e.preventDefault(); FitRecords.deleteFitRecord(f.fit_uuid); }}>
                          ✕
                        </Btn>
                      </label>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                  {/* Client feedback (Compare Fits review, 2-Sep call): "a
                      single custom-system fit produced no comparison graph
                      at all" — the old `< 2` gate (Madhura, 20-Aug: "Comparison
                      only makes sense with 2+ fits") assumed every comparison
                      is an overlay of multiple runs. That's true for the
                      shared-time-axis batch view, but a single PFR/custom
                      fit's OWN parity plot (predicted vs. observed) is still
                      a meaningful, standalone diagnostic — there's nothing to
                      overlay it against, but it isn't nothing. CompareResultsView
                      / ParitySpeciesPanel already render correctly with just
                      one fit's series (one color, one legend entry, one
                      side-by-side panel) — this was purely a UI gate, not a
                      rendering limitation. Lowered to allow 1+ so that case
                      renders instead of staying blocked on a second save.
                      Table/summary rows below also degrade gracefully to a
                      single column. */}
                  <Btn variant="secondary" disabled={selectedFitUuids.length < 1 || loading}
                    onClick={() => FitRecords.runCompare()}>
                    {loading ? 'Comparing…'
                      : selectedFitUuids.length === 1
                        ? `View Selected Fit`
                        : `Compare Selected (${selectedFitUuids.length})`}
                  </Btn>
                  <Btn variant="ghost" disabled={selectedFitUuids.length === 0}
                    onClick={() => (exportAllRef.current ? exportAllRef.current() : FitRecords.downloadSelectedExport())}>
                    ⬇ Export Selected
                  </Btn>
                  <Btn variant="ghost" disabled={selectedFitUuids.length === 0}
                    onClick={() => FitRecords.clearSelection()}>
                    Clear
                  </Btn>
                </div>
                {selectedFitUuids.length === 1 && (
                  <div style={{ fontSize: 12, color: C.textDim, fontFamily: font.ui, marginTop: -6, marginBottom: 14 }}>
                    Showing this fit on its own — select another saved fit too if you want to compare them side by side.
                  </div>
                )}

                {selectedFitUuids.length >= 1 && compareResult && !resultIsCurrent && (
                  <div style={{ fontSize: 13, color: C.textDim, fontFamily: font.ui, padding: '18px 4px' }}>
                    Updating the comparison for your selected fits…
                  </div>
                )}
                {selectedFitUuids.length >= 1 && compareResult && resultIsCurrent && (
                  <CompareResultsView
                    compareResult={compareResult}
                    registeredComponents={speciesCatalog}
                    registerExport={(fn) => { exportAllRef.current = fn; }}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompareFitsPage;